import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaCalendarAlt, FaBell, FaCut, FaCheckCircle, FaMoneyBillWave } from "react-icons/fa";
import moment from "moment";
import { MdDateRange, MdAccessTime } from "react-icons/md";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetGroomingComponent({ appointmentId, doctorId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petGroomings, setPetGroomings] = useState([]);
    const [groomings, setGroomings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingGrooming, setEditingGrooming] = useState(null);
    const [selectedGrooming, setSelectedGrooming] = useState("");
    const [selectedGroomings, setSelectedGroomings] = useState([]);
    const [selectedGroomingRows, setSelectedGroomingRows] = useState([]);
    const [groomingPrice, setGroomingPrice] = useState("");
    const [recomendation, setRecomendation] = useState("");
    const [groomingDate, setGroomingDate] = useState("");
    const [reminderDate, setReminderDate] = useState("");
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [isRecommended, setIsRecommended] = useState(false);

    // Complete modal state
    const [completeModal, setCompleteModal] = useState(false);
    const [selectedCompleteGrooming, setSelectedCompleteGrooming] = useState(null);
    const [completePrice, setCompletePrice] = useState("");
    const [completeReminderDate, setCompleteReminderDate] = useState("");
    const [completeStatus, setCompleteStatus] = useState("pending");
    const [hasGroomingForAppointment, setHasGroomingForAppointment] = useState(false);

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    
    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Delete permission only for Admin
    const canDelete = () => {
        return userRole === 'Admin';
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") return '';
        const num = parseFloat(price);
        if (isNaN(num)) return '';
        return num % 1 === 0 ? num.toString() : num.toFixed(2);
    };

    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showWarningToast = (message) => {
        Swal.fire({
            icon: 'warning',
            title: t('warning'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    // Get reminder status badge color
    const getReminderStatusBadge = (status) => {
        switch(status) {
            case 'completed':
                return 'success';
            case 'pending':
                return 'warning';
            case 'missed':
                return 'danger';
            case 'cancelled':
                return 'secondary';
            default:
                return 'warning';
        }
    };

    useEffect(() => {
        fetchPetGroomings();
    }, [appointmentId, petId]);

    const fetchPetGroomings = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-grooming/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const groomingsForPet = response.data.pet_grooming || [];
                setPetGroomings(groomingsForPet);
                setGroomings(response.data.groomings || []);
                const hasGrooming = groomingsForPet.some(grooming => String(grooming?.appointment_id) === String(appointmentId));
                setHasGroomingForAppointment(hasGrooming);
                // Set current appointment accordion to open by default
                setOpenAccordion(String(appointmentId));
            }
        } catch (error) {
            console.error("Error fetching pet groomings:", error);
            showErrorToast(t('failed to load groomings'));
            setPetGroomings([]);
            setGroomings([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    const findChildGroomingById = (id) => {
        for (const parent of groomings) {
            if (parent.child_groomings) {
                const child = parent.child_groomings.find(c => c.id === id);
                if (child) return child;
            }
        }
        return null;
    };

    const handleGroomingRowChange = (groomingId, field, value) => {
        setSelectedGroomingRows(prevRows => prevRows.map(row => row.grooming_id === groomingId ? {
            ...row,
            [field]: value,
        } : row));
    };

    const removeSelectedGroomingRow = (groomingId) => {
        setSelectedGroomings(prev => prev.filter(id => id !== groomingId));
        setSelectedGroomingRows(prev => prev.filter(row => row.grooming_id !== groomingId));
    };

    const handleGroomingSelect = (selected) => {
        if (editingGrooming) {
            const groomingId = selected?.value;
            setSelectedGrooming(groomingId || "");

            const selectedData = findChildGroomingById(groomingId);
            setGroomingPrice(selectedData ? (selectedData.price ? selectedData.price.toString() : "") : "");
        } else {
            const groomingIds = Array.isArray(selected) ? selected.map(item => item.value) : [];
            setSelectedGroomings(groomingIds);

            setSelectedGroomingRows(prevRows => {
                const nextRows = groomingIds.map(id => {
                    const existing = prevRows.find(row => row.grooming_id === id);
                    const child = findChildGroomingById(id);
                    return {
                        grooming_id: id,
                        grooming_name: child?.name || '',
                        price: existing ? existing.price : (child?.price ? child.price.toString() : ""),
                        grooming_date: existing ? existing.grooming_date : groomingDate || '',
                        reminder_date: existing ? existing.reminder_date : reminderDate || '',
                    };
                });
                return nextRows;
            });
        }
    };

    const handleAddGrooming = () => {
        setEditingGrooming(null);
        setSelectedGrooming("");
        setSelectedGroomings([]);
        setSelectedGroomingRows([]);
        setGroomingPrice("");
        setRecomendation("");
        setGroomingDate("");
        setReminderDate("");
        setGeneralNote("");
        setExistingNoteId(null);
        setIsRecommended(false);
        setShowModal(true);
    };

    const handleEditGrooming = (grooming) => {
        setEditingGrooming(grooming);
        setSelectedGrooming(grooming.grooming_id || "");
        setSelectedGroomings([grooming.grooming_id].filter(Boolean));
        setGroomingPrice(grooming.price !== null && grooming.price !== undefined ? grooming.price.toString() : "");
        setRecomendation(grooming.recomendation || "");
        setGroomingDate(grooming.grooming_date || "");
        setReminderDate(grooming.reminder_date || "");

        setIsRecommended(!!(grooming.recomendation && grooming.recomendation.trim()));

        if (grooming.note && Array.isArray(grooming.note) && grooming.note.length > 0) {
            setGeneralNote(grooming.note[0].note || "");
            setExistingNoteId(grooming.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        setShowModal(true);
    };

    const handleDeleteGrooming = async (groomingId) => {
        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-grooming-delete/${groomingId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetGroomings();
                showSuccessToast(t('Grooming deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting grooming:", error);
            showErrorToast(t('failed to delete grooming'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (groomingId, groomingName) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteGrooming(groomingId);
            }
        });
    };

    const handleOpenComplete = (grooming) => {
        setSelectedCompleteGrooming(grooming);
        setCompletePrice(grooming.price !== null && grooming.price !== undefined ? grooming.price.toString() : "");
        setCompleteReminderDate(grooming.reminder_date || "");
        setCompleteStatus(grooming.reminder_status || "pending");
        setCompleteModal(true);
    };

    const resetCompleteModal = () => {
        setCompleteModal(false);
        setSelectedCompleteGrooming(null);
        setCompletePrice("");
        setCompleteReminderDate("");
        setCompleteStatus("pending");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedCompleteGrooming) {
            if (completeStatus === "completed") {
                const priceNum = completePrice === "" ? 0 : parseFloat(completePrice);
                if (priceNum <= 0) {
                    showWarningToast(t('please_enter_price'));
                    return;
                }
                if (!completeReminderDate) {
                    showWarningToast(t('please_select_reminder_date'));
                    return;
                }
            }
        }

        if (!selectedCompleteGrooming) {
            if (!isRecommended) {
                if (editingGrooming) {
                    if (!selectedGrooming) {
                        showWarningToast(t('please select grooming'));
                        return;
                    }
                } else {
                    if (!selectedGroomings || selectedGroomings.length === 0) {
                        showWarningToast(t('please select grooming'));
                        return;
                    }
                }
            }
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            let url = "";
            let payload = {};

            if (selectedCompleteGrooming) {
                url = `${process.env.REACT_APP_API_URL}/pet-grooming-update/${selectedCompleteGrooming.id}`;
                payload = {
                    appointment_id: appointmentId,
                    is_repeat: true,
                    grooming_id: selectedCompleteGrooming.grooming_id,
                    price: completePrice === "" ? 0 : parseFloat(completePrice) || 0,
                    reminder_date: completeReminderDate,
                    reminder_status: completeStatus,
                };
            } else {
                url = editingGrooming
                    ? `${process.env.REACT_APP_API_URL}/pet-grooming-update/${editingGrooming.id}`
                    : `${process.env.REACT_APP_API_URL}/pet-grooming`;
                payload = {
                    appointment_id: appointmentId,
                    grooming_ids: !isRecommended && !editingGrooming ? selectedGroomings.map(id => parseInt(id, 10)) : undefined,
                    grooming_rows: !isRecommended && !editingGrooming ? selectedGroomingRows.map(row => ({
                        grooming_id: row.grooming_id,
                        price: row.price === "" ? 0 : parseFloat(row.price) || 0,
                        grooming_date: row.grooming_date || null,
                        reminder_date: row.reminder_date || null,
                    })) : undefined,
                    grooming_id: !isRecommended && editingGrooming ? parseInt(selectedGrooming, 10) : undefined,
                    price: editingGrooming ? (groomingPrice === "" ? 0 : parseFloat(groomingPrice) || 0) : undefined,
                    recomendation: isRecommended ? (recomendation || null) : null,
                    grooming_date: editingGrooming ? (groomingDate || null) : (groomingDate || null),
                    reminder_date: editingGrooming ? (reminderDate || null) : (reminderDate || null),
                    is_repeat: false,
                };
            }

            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            } else if (existingNoteId) {
                payload.note = "";
                payload.note_id = existingNoteId;
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data) {
                setShowModal(false);
                if (selectedCompleteGrooming) {
                    resetCompleteModal();
                    showSuccessToast(t('Grooming completed successfully!'));
                } else {
                    showSuccessToast(editingGrooming ? t('Grooming updated successfully!') : t('Grooming added successfully!'));
                }
                fetchPetGroomings();
            }
        } catch (error) {
            console.error("Error saving grooming:", error);
            showErrorToast(error.response?.data?.message || t('failed_to_save_grooming'));
        } finally {
            setSubmitting(false);
        }
    };

    const getGroomingOptions = () => {
        const options = [];
        groomings.forEach(parent => {
            options.push({
                value: null,
                label: parent.name,
                isDisabled: true,
                isParent: true
            });

            if (parent.child_groomings && parent.child_groomings.length > 0) {
                parent.child_groomings.forEach(child => {
                    options.push({
                        value: child.id,
                        label: `   └ ${child.name}`,
                        price: child.price
                    });
                });
            }
        });
        return options;
    };

    const customSelectStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.data.isDisabled ? '#f0f0f0' : state.isFocused ? '#e0e0e0' : 'white',
            color: state.data.isDisabled ? '#999' : '#333',
            fontStyle: state.data.isDisabled ? 'italic' : 'normal',
            fontWeight: state.data.isDisabled ? 'bold' : 'normal',
            cursor: state.data.isDisabled ? 'not-allowed' : 'pointer',
            padding: '10px',
        }),
        control: (provided) => ({
            ...provided,
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#86b7fe'
            }
        })
    };

    const groupGroomingsByAppointment = () => {
        const grouped = {};
        petGroomings.forEach(grooming => {
            const groomingAppointmentId = grooming.appointment_id;
            if (!grouped[groomingAppointmentId]) {
                grouped[groomingAppointmentId] = {
                    appointment: grooming.appointment,
                    groomings: []
                };
            }
            grouped[groomingAppointmentId].groomings.push(grooming);
        });
        return grouped;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading groomings')}</p>
            </div>
        );
    }

    const groomingOptions = getGroomingOptions();
    const selectedOption = groomingOptions.find(opt => opt.value === selectedGrooming);
    const groupedGroomings = groupGroomingsByAppointment();

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('groomings list')}</h6>
                {isAppointmentOpen && !hasGroomingForAppointment &&
                    hasPermission("Appointment Grooming Create") && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddGrooming}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaPlus size={12} /> {t('add grooming')}
                        </Button>
                    )}
            </div>

            {!petGroomings || petGroomings.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaCut size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no groomings found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="appointments-accordion"
                >
                    {Object.entries(groupedGroomings).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, groomings: appointmentGroomings } = appointmentData;
                        const isCurrentAppointment = String(appointmentIdKey) === String(appointmentId);

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <div className="text-muted small">
                                                👨‍⚕️ <strong>Doctor:</strong> {appointment?.doctor?.name || 'Unknown Doctor'}
                                            </div>
                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentGroomings.length} {t('grooming(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-3">
                                    {/* Directly map groomings without nested accordion */}
                                    {appointmentGroomings.map((grooming) => {
                                        if (!grooming) return null;
                                        const dateTime = formatDateTime(grooming.grooming_date);
                                        const noteText = grooming.note && Array.isArray(grooming.note) && grooming.note.length > 0
                                            ? grooming.note[0].note
                                            : null;

                                        return (
                                            <div key={grooming.id} className="card mb-3 border rounded">
                                                <div className="card-header bg-light py-3">
                                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                                            <FaStethoscope className="text-primary" />
                                                            {!grooming.recomendation && (
                                                                <Badge bg="success" className="px-3 py-2">
                                                                    {grooming.grooming?.name || 'N/A'}
                                                                </Badge>
                                                            )}
                                                            {grooming.recomendation && (
                                                                <Badge bg="warning" className="px-3 py-2">
                                                                    {t('Recommended')}
                                                                </Badge>
                                                            )}

                                                            {grooming.grooming_date && (
                                                                <div className="d-flex align-items-center gap-3 text-muted small">
                                                                    <span className="d-flex align-items-center gap-1">
                                                                        <MdDateRange size={14} />
                                                                        {moment(grooming.grooming_date).format("DD MMM YYYY")}
                                                                    </span>
                                                                    <span className="d-flex align-items-center gap-1">
                                                                        <MdAccessTime size={14} />
                                                                        {moment(grooming.grooming_date).format("hh:mm A")}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {/* Reminder Status Badge */}
                                                            {grooming.reminder_status && !grooming.recomendation && (
                                                                <Badge bg={getReminderStatusBadge(grooming.reminder_status)} className="px-3 py-2">
                                                                    <FaBell className="me-1" size={10} />
                                                                    {grooming.reminder_status}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            {isAppointmentOpen &&
                                                                String(grooming.appointment_id) !== String(appointmentId) &&
                                                                grooming.reminder_date &&
                                                                (
                                                                    !grooming.reminder_status ||
                                                                    grooming.reminder_status === 'pending' ||
                                                                    grooming.reminder_status === 'processing' ||
                                                                    grooming.reminder_status === null
                                                                ) &&
                                                                hasPermission("Appointment Grooming edit") && (
                                                                    <Button
                                                                        variant="outline-success"
                                                                        size="sm"
                                                                        onClick={() => handleOpenComplete(grooming)}
                                                                        disabled={grooming.reminder_status === 'completed'}
                                                                        className="d-flex align-items-center gap-1"
                                                                        style={{
                                                                            background: grooming.reminder_status === 'completed' ? '#e2e8f0' : '#38a169',
                                                                            color: grooming.reminder_status === 'completed' ? '#718096' : '#fff',
                                                                            borderColor: grooming.reminder_status === 'completed' ? '#cbd5e0' : '#38a169'
                                                                        }}
                                                                    >
                                                                        <FaCheckCircle size={12} />
                                                                        <span className="d-none d-sm-inline">{t('complete')}</span>
                                                                    </Button>
                                                                )}
                                                            {hasPermission("Appointment Grooming edit") && (
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => handleEditGrooming(grooming)}
                                                                    className="d-flex align-items-center gap-1"
                                                                    style={{ background: '#3182ce', color: '#fff' }}
                                                                >
                                                                    <FaEdit size={12} />
                                                                    <span className="d-none d-sm-inline">{t('edit')}</span>
                                                                </Button>
                                                            )}
                                                            {canDelete() && (
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteClick(grooming.id, grooming.grooming?.name)}
                                                                    className="d-flex align-items-center gap-1"
                                                                >
                                                                    <FaTrash size={12} />
                                                                    <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-body">
                                                    <Row className="g-4">
                                                        <Col md={12}>
                                                            <div className="p-3 bg-light rounded">
                                                                {!grooming.recomendation ? (
                                                                    <>
                                                                        <div className="mb-2">
                                                                            <strong className="text-muted">{t('Grooming Name')}:</strong> {grooming.grooming?.name || 'N/A'}
                                                                        </div>
                                                                        {grooming.grooming?.description && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Description')}:</strong> {grooming.grooming?.description}
                                                                            </div>
                                                                        )}
                                                                        {grooming.grooming_date && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Grooming Date')}:</strong>{' '}
                                                                                {moment(grooming.grooming_date).format("DD MMM YYYY hh:mm A")}
                                                                            </div>
                                                                        )}
                                                                        
                                                                        {grooming.reminder_date && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Reminder Date')}:</strong>{' '}
                                                                                {moment(grooming.reminder_date).format("DD MMM YYYY hh:mm A")}
                                                                            </div>
                                                                        )}
                                                                        {grooming.reminder_status && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Reminder Status')}:</strong>{' '}
                                                                                <Badge bg={getReminderStatusBadge(grooming.reminder_status)}>
                                                                                    {grooming.reminder_status}
                                                                                </Badge>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {grooming.recomendation && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Recommendation')}:</strong> {grooming.recomendation}
                                                                            </div>
                                                                        )}
                                                                        {grooming.grooming_date && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Grooming Date')}:</strong>{' '}
                                                                                {moment(grooming.grooming_date).format("DD MMM YYYY hh:mm A")}
                                                                            </div>
                                                                        )}
                                                                        {grooming.reminder_date && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Reminder Date')}:</strong>{' '}
                                                                                {moment(grooming.reminder_date).format("DD MMM YYYY hh:mm A")}
                                                                            </div>
                                                                        )}
                                                                        {grooming.reminder_status && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Reminder Status')}:</strong>{' '}
                                                                                <Badge bg={getReminderStatusBadge(grooming.reminder_status)}>
                                                                                    {grooming.reminder_status}
                                                                                </Badge>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {noteText && (
                                                                    <div className="mt-3 pt-2 border-top">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <FaFileAlt className="text-success" />
                                                                            <strong className="text-muted">{t('note')}:</strong>
                                                                        </div>
                                                                        <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => {
                setShowModal(false);
                setSelectedCompleteGrooming(null);
            }} size="xl" centered>
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>
                        {editingGrooming ? t('Edit Grooming') : t('Add Grooming')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="recommended-checkbox"
                                label={<span className="fw-bold">{t('Recommended')}</span>}
                                checked={isRecommended}
                                onChange={(e) => setIsRecommended(e.target.checked)}
                            />
                        </Form.Group>

                        {!isRecommended ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {editingGrooming ? t('Select Grooming') : t('Select Groomings')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        options={groomingOptions}
                                        value={editingGrooming ? selectedOption : groomingOptions.filter(opt => selectedGroomings.includes(opt.value))}
                                        onChange={handleGroomingSelect}
                                        placeholder={t('select grooming')}
                                        isSearchable
                                        isMulti={!editingGrooming}
                                        styles={customSelectStyles}
                                        formatOptionLabel={(option) => (
                                            <span style={{
                                                marginLeft: option.isParent ? '0' : '20px',
                                                fontWeight: option.isParent ? 'bold' : 'normal',
                                                color: option.isParent ? '#666' : '#333'
                                            }}>
                                                {option.label}
                                            </span>
                                        )}
                                    />
                                    {!editingGrooming && (
                                        <Form.Text className="text-muted">
                                            {t('You can select multiple groomings to add at once.')}
                                        </Form.Text>
                                    )}
                                </Form.Group>

                                {selectedGroomingRows.length > 0 && !editingGrooming && (
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>{t('Selected Groomings')}</strong>
                                            <small className="text-muted">{t('Edit price, grooming date and reminder date before saving')}</small>
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table table-bordered table-striped text-nowrap mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t('Grooming Name')}</th>
                                                        <th>{t('Price')}</th>
                                                        <th>{t('Reminder Date')}</th>
                                                        <th>{t('Action')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedGroomingRows.map((row) => (
                                                        <tr key={row.grooming_id}>
                                                            <td className="align-middle fw-semibold">{row.grooming_name || t('Grooming')}</td>
                                                            <td>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={row.price ?? ''}
                                                                    onFocus={(e) => e.target.select()}
                                                                    onChange={(e) => handleGroomingRowChange(row.grooming_id, 'price', e.target.value)}
                                                                    placeholder={t('Price')}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="datetime-local"
                                                                    value={row.reminder_date}
                                                                    onChange={(e) => handleGroomingRowChange(row.grooming_id, 'reminder_date', e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="text-center align-middle">
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => removeSelectedGroomingRow(row.grooming_id)}
                                                                >
                                                                    {t('Remove')}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {editingGrooming && !isRecommended && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold">
                                                <FaMoneyBillWave className="text-success me-1" /> {t('Price')}
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                onFocus={(e) => e.target.select()}
                                                value={groomingPrice ?? ''}
                                                onChange={(e) => setGroomingPrice(e.target.value)}
                                                placeholder={t('Enter price')}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                                <FaCalendarAlt /> {t('Grooming Date')}
                                            </Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                value={groomingDate}
                                                onChange={(e) => setGroomingDate(e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                                <FaBell /> {t('Reminder Date')}
                                            </Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                value={reminderDate}
                                                onChange={(e) => setReminderDate(e.target.value)}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t('Recommendations')}
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={recomendation}
                                        onChange={(e) => setRecomendation(e.target.value)}
                                        placeholder={t('Enter recommendations here...')}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                        <FaCalendarAlt /> {t('Grooming Date')}
                                    </Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={groomingDate}
                                        onChange={(e) => setGroomingDate(e.target.value)}
                                    />
                                </Form.Group>
                            </>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="p-3">
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('cancel')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingGrooming ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Complete Modal */}
            <Modal show={completeModal} onHide={resetCompleteModal}>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('Complete Grooming')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Status')}</Form.Label>
                        <Form.Select
                            value={completeStatus}
                            onChange={(e) => setCompleteStatus(e.target.value)}
                        >
                            <option value="pending">{t('Pending')}</option>
                            <option value="completed">{t('Completed')}</option>
                            <option value="missed">{t('Missed')}</option>
                            <option value="cancelled">{t('Cancelled')}</option>
                        </Form.Select>
                    </Form.Group>

                    <div className="mb-3">
                        <Badge bg={getReminderStatusBadge(completeStatus)} className="px-3 py-2">
                            {completeStatus === "completed" ? t('Completed') : completeStatus === "pending" ? t('Pending Completion') : completeStatus === "missed" ? t('Missed') : t('Cancelled')}
                        </Badge>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('Grooming Name')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={selectedCompleteGrooming?.grooming?.name || ""}
                            disabled
                        />
                    </Form.Group>

                    {completeStatus === "completed" && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Price')}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={completePrice ?? ''}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => setCompletePrice(e.target.value)}
                                    placeholder={t('Enter price')}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>{t('Next Reminder Date')}</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    value={completeReminderDate}
                                    onChange={(e) => setCompleteReminderDate(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="p-2">
                    <Button variant="secondary" onClick={resetCompleteModal}>
                        {t('cancel')}
                    </Button>
                    <Button variant="success" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? <Spinner animation="border" size="sm" /> : t('Save & Complete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}